import { baseApi } from "./api";
import request from "../request";

export const costTrackingApi = baseApi.injectEndpoints({
	endpoints: (build) => ({
		getCostTracking: build.query({
			query: (projectId) => ({
				url: `/projects/${projectId}/cost-tracking`,
			}),
			providesTags: (_result, _error, projectId) => [
				{ type: "CostTracking", id: projectId },
			],
		}),
		getCostTrackingVersions: build.query({
			query: (projectId) => ({
				url: `/projects/${projectId}/cost-tracking/versions`,
			}),
			providesTags: (_result, _error, projectId) => [
				{ type: "CostTracking", id: `versions-${projectId}` },
			],
		}),
		getCostTrackingById: build.query({
			query: ({ projectId, costTrackingId }) => ({
				url: `/projects/${projectId}/cost-tracking/${costTrackingId}`,
			}),
			providesTags: (_result, _error, { costTrackingId }) => [
				{ type: "CostTracking", id: costTrackingId },
			],
		}),
		updateCostTracking: build.mutation({
			query: ({ projectId, costTrackingId, costTrackingData }) => ({
				url: `/projects/${projectId}/cost-tracking/${costTrackingId}`,
				method: "PUT",
				body: { costTrackingData },
			}),
			invalidatesTags: (_result, _error, { projectId }) => [
				{ type: "CostTracking", id: projectId },
			],
		}),
		refreshCostTracking: build.mutation({
			query: ({ projectId, costTrackingId }) => ({
				url: `/projects/${projectId}/cost-tracking/${costTrackingId}/refresh`,
				method: "POST",
			}),
			invalidatesTags: (_result, _error, { projectId }) => [
				{ type: "CostTracking", id: projectId },
				{ type: "CostTracking", id: `versions-${projectId}` },
			],
		}),
		createManualEntry: build.mutation({
			query: ({ projectId, costTrackingId, ...entryData }) => ({
				url: `/projects/${projectId}/cost-tracking/${costTrackingId}/manual-entries`,
				method: "POST",
				body: entryData,
			}),
			invalidatesTags: (_result, _error, { projectId, costTrackingId }) => [
				{ type: "CostTracking", id: projectId },
				{ type: "ManualEntry", id: costTrackingId },
			],
		}),
		getManualEntries: build.query({
			query: ({ projectId, costTrackingId }) => ({
				url: `/projects/${projectId}/cost-tracking/${costTrackingId}/manual-entries`,
			}),
			providesTags: (_result, _error, { costTrackingId }) => [
				{ type: "ManualEntry", id: costTrackingId },
			],
		}),
		updateManualEntry: build.mutation({
			query: ({ projectId, entryId, ...entryData }) => ({
				url: `/projects/${projectId}/cost-tracking/manual-entries/${entryId}`,
				method: "PUT",
				body: entryData,
			}),
			invalidatesTags: (_result, _error, { projectId }) => [
				{ type: "CostTracking", id: projectId },
				{ type: "ManualEntry" },
			],
		}),
		deleteManualEntry: build.mutation({
			query: ({ projectId, entryId }) => ({
				url: `/projects/${projectId}/cost-tracking/manual-entries/${entryId}`,
				method: "DELETE",
			}),
			invalidatesTags: (_result, _error, { projectId }) => [
				{ type: "CostTracking", id: projectId },
				{ type: "ManualEntry" },
			],
		}),
	}),
});

export const {
	useGetCostTrackingQuery,
	useGetCostTrackingVersionsQuery,
	useGetCostTrackingByIdQuery,
	useUpdateCostTrackingMutation,
	useRefreshCostTrackingMutation,
	useCreateManualEntryMutation,
	useGetManualEntriesQuery,
	useUpdateManualEntryMutation,
	useDeleteManualEntryMutation,
} = costTrackingApi;

export const downloadCostTrackingPdf = async (
	projectId,
	language = "de",
	costTrackingId = null,
) => {
	try {
		const config = {
			responseType: "blob",
			headers: {
				Accept: "application/pdf",
			},
		};

		let apiUrl = `/projects/${projectId}/cost-tracking/download?lang=${language}`;
		if (costTrackingId) {
			apiUrl += `&costTrackingId=${costTrackingId}`;
		}

		const response = await request.get(apiUrl, config);

		const blob = new Blob([response], { type: "application/pdf" });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", `cost-tracking-${projectId}.pdf`);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);

		return response;
	} catch (error) {
		console.error("Error downloading cost tracking PDF:", error);
		throw new Error(
			error?.message || "Error downloading cost tracking PDF",
		);
	}
};

export const downloadCostTracking = async (
	projectId,
	options = {},
	costTrackingId = null,
) => {
	const {
		language = "de",
		format = "pdf",
		filename = null,
		onlyWithData = true,
		showGross = true,
	} = options;
	const isExcel = format === "excel";

	try {
		const mimeType = isExcel
			? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
			: "application/pdf";

		const config = {
			responseType: "blob",
			headers: { Accept: mimeType },
		};

		const payload = {
			language,
			format,
			costTrackingId: costTrackingId || undefined,
			filename: filename || undefined,
			onlyWithData,
			showGross,
		};

		const response = await request.post(
			`/projects/${projectId}/cost-tracking/download`,
			payload,
			config,
		);

		const blob = new Blob([response], { type: mimeType });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		const ext = isExcel ? ".xlsx" : ".pdf";
		link.setAttribute(
			"download",
			filename || `cost-tracking-${projectId}${ext}`,
		);
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);

		return response;
	} catch (error) {
		console.error("Error downloading cost tracking:", error);
		throw new Error(error?.message || "Error downloading cost tracking");
	}
};
